// Темы оформления в стиле Telegram. Тема — набор CSS-переменных,
// переключаемых атрибутом data-theme на <html> (блоки в styles.css).
// Выбор хранится в localStorage: оформление — предпочтение браузера,
// а не аккаунта, поэтому на сервер его не отправляем.

/** Реальные темы, у которых есть CSS-блок. */
export type ResolvedThemeId = "day" | "dark" | "oled";
/** Пользовательский выбор: конкретная тема или «как в системе». */
export type ThemeId = ResolvedThemeId | "system";

export interface ThemeOption {
  id: ThemeId;
  label: string;
  /** Цвета мини-превью в настройках (как карточки тем в Telegram).
      wall принимает любой CSS background (в т.ч. градиент). */
  preview: {
    wall: string;
    bubbleIn: string;
    bubbleOut: string;
  };
}

export const THEME_OPTIONS: ThemeOption[] = [
  {
    id: "day",
    label: "Светлая",
    preview: { wall: "#a3c294", bubbleIn: "#ffffff", bubbleOut: "#eeffde" }
  },
  {
    id: "dark",
    label: "Тёмная",
    preview: { wall: "#17151f", bubbleIn: "#212121", bubbleOut: "#8774e1" }
  },
  {
    id: "oled",
    label: "OLED",
    preview: { wall: "#000000", bubbleIn: "#141414", bubbleOut: "#8774e1" }
  },
  {
    id: "system",
    label: "Системная",
    preview: {
      wall: "linear-gradient(115deg, #a3c294 50%, #17151f 50%)",
      bubbleIn: "#ffffff",
      bubbleOut: "#8774e1"
    }
  }
];

const STORAGE_KEY = "tg-client:theme";
const DEFAULT_THEME: ThemeId = "dark";

/* PWA-цвет строки состояния должен совпадать с шапкой темы. */
const THEME_META_COLOR: Record<ResolvedThemeId, string> = {
  day: "#ffffff",
  dark: "#212121",
  oled: "#000000"
};

const isThemeId = (value: unknown): value is ThemeId =>
  value === "day" || value === "dark" || value === "oled" || value === "system";

export const readStoredTheme = (): ThemeId => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isThemeId(stored) ? stored : DEFAULT_THEME;
};

const systemMediaQuery = (): MediaQueryList | null =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: light)")
    : null;

const resolveTheme = (theme: ThemeId): ResolvedThemeId => {
  if (theme !== "system") {
    return theme;
  }
  return systemMediaQuery()?.matches ? "day" : "dark";
};

const applyResolvedTheme = (resolved: ResolvedThemeId): void => {
  document.documentElement.dataset.theme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_META_COLOR[resolved]);
};

/* Пока выбрана «Системная», следим за сменой темы ОС на лету. */
let systemListener: ((event: MediaQueryListEvent) => void) | null = null;

const detachSystemListener = (): void => {
  if (systemListener) {
    systemMediaQuery()?.removeEventListener("change", systemListener);
    systemListener = null;
  }
};

export const applyTheme = (theme: ThemeId): void => {
  if (typeof document === "undefined") {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, theme);
  applyResolvedTheme(resolveTheme(theme));

  detachSystemListener();
  if (theme === "system") {
    systemListener = (event) => {
      applyResolvedTheme(event.matches ? "day" : "dark");
    };
    systemMediaQuery()?.addEventListener("change", systemListener);
  }
};
